/**
 * Lazy-loaded icons with encapsulated Suspense boundaries.
 */
import React, { Suspense } from 'react';

// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazyForPaint } from '@atlassian/react-loosely-lazy';

import { withBrandIcon, type BrandIcon } from '../../../icons/with-brand-icon';

const OfficialIcon = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "@atlassian/logo-third-party/clickup" */ '@atlassian/logo-third-party/clickup'
		).then((m) => m.ClickupIcon),
	{ moduleId: '@atlassian/logo-third-party/clickup' },
);

const LazyClickUpIconComponent = lazyForPaint(
	() => import(/* webpackChunkName: "icon-clickup" */ './utils').then((m) => m.ClickUpIcon),
	{ moduleId: '@atlassian/rovo-connectors-config/clickup/icon' },
);

const LazyClickUpSearchResultIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-clickup-search-result" */ './utils').then(
			(m) => m.ClickUpSearchResultIcon,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/clickup/search-result-icon' },
);

const LazyClickUpIconBorderlessComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-clickup-borderless" */ './utils').then(
			(m) => m.ClickUpIconBorderless,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/clickup/icon-borderless' },
);

/**
 * Exported icons with self-contained Suspense boundaries.
 * Consumers can use these directly without wrapping in LazySuspense.
 */
export const ClickUpIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyClickUpIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'small',
);

export const ClickUpSearchResultIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyClickUpSearchResultIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'medium',
);

export const ClickUpIconBorderless: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyClickUpIconBorderlessComponent />
		</Suspense>
	),
	OfficialIcon,
	'xxsmall',
);
